import type {
  ListWelloCommentDto,
  ListWelloCommentResponse,
} from '__generated__/api/unauth';
import { hasObjectKey } from '@core/utils';

import { UnauthApi } from '@/api';

import type { COMMENT_TYPE } from './types';
import { COMMENT_SORT_TYPE, COMMENT_STATUS } from './types';

export interface CommentData {
  commentList: Comment[];
  totalCount: number;
  totalRootCount: number;
  visibleTotalCount: number;
}

const normalizedComment = (comment: ListWelloCommentDto, userId?: number) => ({
  contentId: comment.contentId,
  parentNickName: comment.parentNickName,
  commentId: comment.welloCommentId,
  commentIdIdx: comment.contentIdIdx,
  createdAt: comment.dateTime,
  commentType: comment.commentTypeValue,
  isBlocked: comment.blockYN,
  isDisplay:
    comment.commentStatusCd === COMMENT_STATUS.NON_DISPLAY ? false : true,
  isReport: comment.reportYN,
  commentTypeCd: comment.commentTypeCd,
  thumbUpCount: comment.likeCount,
  isDeleted: comment.commentStatusCd === COMMENT_STATUS.DELETED,
  contents: comment.contents,
  nickname: comment.nickName,
  profileImage: comment.profileImage,
  isMine: comment.createBy === userId,
  isMyThumbUp: comment.likeYN,
  parentCommentId: comment.parentWelloCommentId,
  rootCommentId: comment.rootWelloCommentId,
  isBest: comment.bestYN,
  isPicked: comment.commentPickYN,
  isWelloHelper: comment.welloHelperYN,
  relationPolicyList: comment.relationPolicyList,
  hasRelativePolicy: !!comment.relationPolicyList?.length,
});

type NormalizedComment = ReturnType<typeof normalizedComment>;

export interface Comment extends NormalizedComment {
  childCommentList?: NormalizedComment[];
}

export const selectCommentList = (
  context?: Partial<ListWelloCommentResponse>,
  userId?: number,
): CommentData => {
  if (!context || !context.contents) {
    return {
      commentList: [],
      totalCount: 0,
      totalRootCount: 0,
      visibleTotalCount: 0,
    };
  }
  const classifyCommentList = (commentList: ListWelloCommentDto[]) =>
    commentList.reduce((acc, comment) => {
      const transformedComment = normalizedComment(comment, userId);

      if (!comment.rootWelloCommentId) {
        acc.push({ ...transformedComment, childCommentList: [] });
      } else {
        const rootComment = acc.find(
          ({ commentId }) => commentId === comment.rootWelloCommentId,
        );
        rootComment?.childCommentList?.push(transformedComment);
      }

      return acc;
    }, [] as Comment[]);

  const commentList = classifyCommentList(context.contents);

  const specificCommentList = context.specificComment
    ? classifyCommentList(
        context.specificComment.map(
          ({ parentNickName: parent_nick_name = '', ...commonValues }) => ({
            commentCount: 0,
            parentNickName: parent_nick_name,
            ...commonValues,
          }),
        ),
      )
    : [];

  return {
    commentList: specificCommentList.concat(commentList),
    totalRootCount: context.rootTotalCount ?? 0,
    totalCount: context.totalCount ?? 0,
    visibleTotalCount: context.visibleTotalCount ?? 0,
  };
};

// 정책카드는 UUID(string), 그 외 컨텐츠는 숫자 ID. 백엔드가 두 필드를 분리해서 받는다.
export const toContentIdQuery = (id: number | string) =>
  typeof id === 'string' ? { contentUuid: id } : { contentId: id };

interface BuildOptimisticCommentParams {
  welloCommentId: number;
  contents: string;
  commentType: COMMENT_TYPE;
  contentId: number | string;
  parentCommentId?: number;
  rootCommentId?: number;
  /** 답글일 때 멘션 대상(부모 작성자) 닉네임 */
  replyTo?: string;
  author: { id: number; nickName?: string; profileImage?: string };
}

// POST 응답이 새 댓글 id만 주므로, 목록 반영 전까지 보여줄 목록 DTO를 입력값과 내 프로필로 합성한다.
// 서버 반영이 확인되면 refetch가 이 항목을 실데이터로 교체한다.
export const buildOptimisticComment = ({
  welloCommentId,
  contents,
  commentType,
  contentId,
  parentCommentId,
  rootCommentId,
  replyTo,
  author,
}: BuildOptimisticCommentParams): ListWelloCommentDto => ({
  welloCommentId,
  rootWelloCommentId: rootCommentId,
  parentWelloCommentId: parentCommentId,
  parentNickName: replyTo ?? '',
  commentTypeCd: commentType,
  commentTypeValue: '',
  commentStatusCd: COMMENT_STATUS.DISPLAY,
  commentStatusValue: '',
  profileImage: author.profileImage,
  nickName: author.nickName ?? '',
  contentId: typeof contentId === 'number' ? contentId : undefined,
  contents,
  likeCount: 0,
  commentCount: 0,
  likeYN: false,
  depth: rootCommentId ? 2 : 1,
  bestYN: false,
  createBy: author.id,
  dateTime: '방금 전',
  reportYN: false,
  blockYN: false,
  welloHelperYN: false,
  commentPickYN: false,
  relationPolicyList: [],
});

interface CommentListEnvelope {
  context?: Partial<ListWelloCommentResponse>;
}

const containsComment = (
  contents: ListWelloCommentDto[],
  welloCommentId: number,
) => contents.some((comment) => comment.welloCommentId === welloCommentId);

const insertCommentIntoEnvelope = (
  envelope: CommentListEnvelope,
  newComment: ListWelloCommentDto,
): CommentListEnvelope | undefined => {
  const { context } = envelope;
  const contents = Array.isArray(context?.contents)
    ? context.contents
    : undefined;

  if (!context || !contents) {
    return undefined;
  }
  if (containsComment(contents, newComment.welloCommentId)) {
    return undefined;
  }

  const { rootWelloCommentId } = newComment;

  // 답글은 부모 루트가 같은 응답 안에 있어야 중첩 렌더된다. 없으면 서버 반영 후 refetch에 맡긴다.
  if (rootWelloCommentId && !containsComment(contents, rootWelloCommentId)) {
    return undefined;
  }

  return {
    ...envelope,
    context: {
      ...context,
      contents: rootWelloCommentId
        ? [...contents, newComment]
        : [newComment, ...contents],
      totalCount: (context.totalCount ?? 0) + 1,
      visibleTotalCount: (context.visibleTotalCount ?? 0) + 1,
      rootTotalCount:
        (context.rootTotalCount ?? 0) + (rootWelloCommentId ? 0 : 1),
    },
  };
};

/**
 * 댓글 목록 캐시(페이지네이션 단일 응답·무한 스크롤 pages)에 낙관적 댓글을 삽입한다.
 * 이미 있거나(멱등) 삽입할 자리가 없으면 undefined를 반환해 캐시를 건드리지 않는다.
 */
export const insertCommentIntoListData = (
  data: unknown,
  newComment: ListWelloCommentDto,
) => {
  if (!data || typeof data !== 'object') {
    return undefined;
  }

  if (hasObjectKey(data, 'pages') && Array.isArray(data.pages)) {
    const pages = data.pages as CommentListEnvelope[];

    let insertedPageIndex = -1;
    const nextPages = pages.map((page, pageIndex) => {
      if (insertedPageIndex !== -1) {
        return page;
      }
      // 루트 댓글은 최신순 1페이지에만 들어간다. 답글은 부모 루트가 있는 페이지를 찾는다.
      if (!newComment.rootWelloCommentId && pageIndex > 0) {
        return page;
      }

      const insertedPage = insertCommentIntoEnvelope(page, newComment);

      if (!insertedPage) {
        return page;
      }

      insertedPageIndex = pageIndex;

      return insertedPage;
    });

    return insertedPageIndex === -1 ? undefined : { ...data, pages: nextPages };
  }

  return insertCommentIntoEnvelope(data as CommentListEnvelope, newComment);
};

export type CommentListItemPatch = Partial<ListWelloCommentDto>;

const patchCommentInEnvelope = (
  envelope: CommentListEnvelope,
  commentId: number,
  patch: CommentListItemPatch,
): CommentListEnvelope | undefined => {
  const { context } = envelope;
  const contents = Array.isArray(context?.contents)
    ? context.contents
    : undefined;

  if (!context || !contents || !containsComment(contents, commentId)) {
    return undefined;
  }

  return {
    ...envelope,
    context: {
      ...context,
      contents: contents.map((comment) =>
        comment.welloCommentId === commentId
          ? { ...comment, ...patch }
          : comment,
      ),
    },
  };
};

/**
 * 목록 캐시 안의 특정 댓글에 부분 패치를 적용한다(수정/삭제/신고 낙관 반영).
 * 대상이 없으면 undefined를 반환해 캐시를 건드리지 않는다.
 */
export const patchCommentInListData = (
  data: unknown,
  commentId: number,
  patch: CommentListItemPatch,
) => {
  if (!data || typeof data !== 'object') {
    return undefined;
  }

  if (hasObjectKey(data, 'pages') && Array.isArray(data.pages)) {
    const pages = data.pages as CommentListEnvelope[];

    let didPatch = false;
    const nextPages = pages.map((page) => {
      const patchedPage = patchCommentInEnvelope(page, commentId, patch);

      if (patchedPage) {
        didPatch = true;
      }

      return patchedPage ?? page;
    });

    return didPatch ? { ...data, pages: nextPages } : undefined;
  }

  return patchCommentInEnvelope(data as CommentListEnvelope, commentId, patch);
};

// 캐시 데이터에서 댓글이 들어 있는 페이지 번호를 찾는다(반영 확인 probe 대상 페이지).
export const findCommentPage = (data: unknown, commentId: number) => {
  const findInEnvelope = (
    envelope: CommentListEnvelope,
    fallbackPage: number,
  ) => {
    const { context } = envelope;
    const contents = Array.isArray(context?.contents)
      ? context.contents
      : undefined;

    if (!contents || !containsComment(contents, commentId)) {
      return undefined;
    }

    return context?.page ?? fallbackPage;
  };

  if (!data || typeof data !== 'object') {
    return undefined;
  }

  if (hasObjectKey(data, 'pages') && Array.isArray(data.pages)) {
    const pages = data.pages as CommentListEnvelope[];

    for (const [pageIndex, page] of pages.entries()) {
      const foundPage = findInEnvelope(page, pageIndex + 1);

      if (foundPage !== undefined) {
        return foundPage;
      }
    }

    return undefined;
  }

  return findInEnvelope(data as CommentListEnvelope, 1);
};

interface GetCommentListProps {
  id: number | string;
  commentType: COMMENT_TYPE;
  sortType?: COMMENT_SORT_TYPE;
}

export const getCommentListPage = async ({
  commentType,
  id,
}: GetCommentListProps) => {
  const { data } = await UnauthApi['get:/wello/v2/allows/wello-comment']({
    query: {
      page: 1,
      size: 100,
      commentTypeCd: commentType,
      commentSort: COMMENT_SORT_TYPE.LATEST,
      ...toContentIdQuery(id),
    },
  });

  return data;
};

export type CommentListPage = Awaited<ReturnType<typeof getCommentListPage>>;
